import React, { useRef, useEffect } from 'react';
import { Box, Typography, CircularProgress, Avatar } from '@mui/material';
import TassMessage from './TassMessage';
import TassTaskBoundary from './TassTaskBoundary';
import TassReviewProgress from './TassReviewProgress';

function TassChatArea({ messages, isLoading, onConfirmTool, onFeedback, onSelectClient, reviewJob, token }) {
  const messagesEndRef = useRef(null);
  const containerRef = useRef(null);

  // Auto-scroll to bottom on new messages or loading state change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isLoading]);

  const renderItem = (item, index) => {
    // Task boundary separators
    if (item._type === 'task_boundary') {
      return (
        <TassTaskBoundary
          key={`boundary-${index}`}
          topic={item.topic}
          timestamp={item.timestamp}
        />
      );
    }

    // Regular message
    return (
      <TassMessage
        key={item._id || `msg-${index}`}
        message={item}
        onConfirmTool={onConfirmTool}
        onFeedback={item.role === 'tass' ? (text) => onFeedback(index, text) : undefined}
        onSelectClient={onSelectClient}
      />
    );
  };

  return (
    <Box
      ref={containerRef}
      sx={{
        flex: 1,
        overflowY: 'auto',
        py: 1.5,
        bgcolor: 'background.default',
        '&::-webkit-scrollbar': { width: 6 },
        '&::-webkit-scrollbar-thumb': { backgroundColor: 'grey.300', borderRadius: 3 }
      }}
    >
      {/* Empty state */}
      {messages.length === 0 && !isLoading && (
        <Box sx={{ textAlign: 'center', mt: 6, px: 3 }}>
          <Avatar src="/tass-avatar.jpg?v=2" alt="Tass" sx={{ width: 40, height: 40, mb: 1.5, mx: 'auto' }} />
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
            Ask TASS anything about your caseload
          </Typography>
          <Typography variant="caption" color="text.disabled">
            Try "Who needs attention?" or "How is Sarah doing?"
          </Typography>
        </Box>
      )}

      {/* Messages */}
      {messages.map((item, index) => renderItem(item, index))}

      {/* Review progress inline */}
      {reviewJob && reviewJob.status === 'processing' && (
        <Box sx={{ px: 2, my: 1 }}>
          <TassReviewProgress
            jobId={reviewJob.jobId}
            token={token}
            onComplete={reviewJob.onComplete}
          />
        </Box>
      )}

      {/* Loading indicator */}
      {isLoading && (
        <Box sx={{ display: 'flex', alignItems: 'center', px: 2, mb: 1.5 }}>
          <Avatar src="/tass-avatar.jpg?v=2" alt="Tass" sx={{ width: 28, height: 28, mr: 0.75 }} />
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              p: 1,
              borderRadius: 2,
              bgcolor: 'grey.50'
            }}
          >
            <CircularProgress size={14} />
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
              TASS is thinking...
            </Typography>
          </Box>
        </Box>
      )}

      <div ref={messagesEndRef} />
    </Box>
  );
}

export default TassChatArea;
